import { Link } from "react-router-dom";
import Badge from "../../components/ui/Badge";
import { formatDate } from "../../utils/date";
import { todayManilaKey } from "../../utils/manilaTime";
import { getEmployeeId } from "../../utils/session";
import { cx } from "../../utils/classNames";
import {
  ACTION_STATUSES,
  EMPLOYEE_STATUS,
  getPersonName,
  isOverdue,
} from "./performanceUtils";
import { useMyEvaluations } from "./usePerformanceQueries";
import QueryState from "./components/QueryState";
import ui from "../../components/ui/ui.module.css";
import styles from "./Performance.module.css";

const MyEvaluationsView = () => {
  const query = useMyEvaluations();
  const today = todayManilaKey();

  if (!getEmployeeId()) {
    return (
      <div className={cx(ui.alert, ui.alertGray)}>
        Your account is not linked to an employee record.
      </div>
    );
  }

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <h4 className="page-title">My Evaluations</h4>
          <p className="page-subtitle">
            Your performance evaluations, self-assessments and results.
          </p>
        </div>
      </div>

      <QueryState query={query} loadingText="Loading your evaluations…">
        {(evaluations) => {
          if (!evaluations.length) {
            return (
              <div className={cx(ui.card, ui.empty)}>
                You have no performance evaluations yet.
              </div>
            );
          }

          // Items needing my action first; the API already sorts by newest period.
          const sorted = [...evaluations].sort(
            (a, b) =>
              Number(ACTION_STATUSES.includes(b.status)) -
              Number(ACTION_STATUSES.includes(a.status)),
          );

          return (
            <div className={styles.evaluationCards}>
              {sorted.map((evaluation) => {
                const status = EMPLOYEE_STATUS[evaluation.status] ?? {
                  label: evaluation.status,
                  tone: "gray",
                };
                const overdue = isOverdue(evaluation, today);

                return (
                  <Link
                    key={evaluation.id}
                    to={`/me/evaluations/${evaluation.id}`}
                    className={cx(
                      styles.evaluationCard,
                      status.action && styles.evaluationCardAction,
                    )}
                  >
                    <div>
                      <div className={styles.cardTitle}>
                        {evaluation.template?.name}
                      </div>
                      <div className={ui.muted}>{evaluation.periodLabel}</div>
                    </div>

                    <Badge tone={status.tone}>{status.label}</Badge>

                    <div className={ui.fieldGrid}>
                      <div>
                        <div className={ui.fieldLabel}>Evaluator</div>
                        <div className={ui.fieldValue}>
                          {getPersonName(evaluation.evaluator)}
                        </div>
                      </div>
                      <div>
                        <div className={ui.fieldLabel}>Due</div>
                        <div
                          className={cx(
                            ui.fieldValue,
                            overdue && styles.overdue,
                          )}
                        >
                          {evaluation.dueDate
                            ? formatDate(evaluation.dueDate)
                            : "—"}
                        </div>
                      </div>
                      {evaluation.totalScore != null && (
                        <div>
                          <div className={ui.fieldLabel}>Score</div>
                          <div className={ui.fieldValue}>
                            {evaluation.totalScore} / 100{" "}
                            {evaluation.ratingBand && (
                              <Badge tone="blue">{evaluation.ratingBand}</Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {status.action && (
                      <span
                        className={cx(ui.button, ui.primary, ui.buttonSmall)}
                      >
                        {status.action} <i className="bi bi-arrow-right"></i>
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        }}
      </QueryState>
    </div>
  );
};

export default MyEvaluationsView;
